import { useState, useEffect, useCallback } from 'react'
import { FiCheck, FiX, FiTrash2, FiPlus, FiInbox } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { Card, Btn, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'
import { resumirOperacion, estadoCorreccionPill } from '../../utils/correcciones'
import CorreccionDiaModal from './CorreccionDiaModal'

/*
 * CorreccionesDiaPanel — Punto C (vista empleado).
 * Surface del modelo nuevo correcciones_dia para el empleado:
 *  - Propuestas del administrador pendientes de su confirmación (aceptar/rechazar).
 *  - Sus propias solicitudes (estado + cancelar si están pendientes).
 *  - Botón "Solicitar corrección" que abre la pantalla unificada.
 *
 * Props:
 *  - onCambio?: () => void   (para refrescar el historial del padre tras aprobar/cancelar)
 */
const CorreccionesDiaPanel = ({ onCambio }) => {
  const [propuestas, setPropuestas] = useState([])
  const [solicitudes, setSolicitudes] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [procesando, setProcesando] = useState(null)
  const [rechazoId, setRechazoId] = useState(null)
  const [comentarioRechazo, setComentarioRechazo] = useState('')

  const cargar = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/correcciones/dia/mis-propuestas', { params: { estado: 'pendiente' } }),
        api.get('/correcciones/dia/mis-solicitudes'),
      ])
      setPropuestas(p.data || [])
      setSolicitudes(s.data || [])
    } catch { /* silencioso */ }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const responder = async (id, accion, comentario = '') => {
    setProcesando(id)
    try {
      await api.post(`/correcciones/dia/${id}/responder`, { accion, comentario })
      toast.success(accion === 'aprobar' ? 'Propuesta aceptada' : 'Propuesta rechazada')
      cargar(); onCambio?.()
    } catch (e) {
      toast.error(e.message || 'No se pudo procesar')
    } finally {
      setProcesando(null); setRechazoId(null); setComentarioRechazo('')
    }
  }

  const rechazar = (id) => {
    if (rechazoId === id) {
      responder(id, 'rechazar', comentarioRechazo)
    } else {
      setRechazoId(id); setComentarioRechazo('')
    }
  }

  const cancelar = async (id) => {
    if (!window.confirm('¿Cancelar esta solicitud de corrección?')) return
    setProcesando(id)
    try {
      await api.delete(`/correcciones/dia/${id}`)
      toast.success('Solicitud cancelada')
      cargar(); onCambio?.()
    } catch (e) {
      toast.error(e.message || 'No se pudo cancelar')
    } finally { setProcesando(null) }
  }

  const ListaOperaciones = ({ ops }) => (
    <ul className="space-y-0.5 text-[12px] text-[var(--ink-2)] mt-1">
      {(ops || []).map((op, i) => <li key={i}>· {resumirOperacion(op)}</li>)}
    </ul>
  )

  return (
    <>
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-[16px] text-[var(--ink)]">Correcciones de marcajes</h3>
          <Btn type="button" variant="primary" onClick={() => setModalOpen(true)} className="!py-1.5 !px-3 text-[13px] flex items-center gap-1.5">
            <FiPlus size={13} /> Solicitar corrección
          </Btn>
        </div>

        {/* Propuestas del administrador pendientes de confirmación */}
        {propuestas.length > 0 && (
          <div className="mb-4">
            <div className="eyebrow mb-2">El administrador propone ({propuestas.length})</div>
            <div className="space-y-2">
              {propuestas.map((p) => (
                <div key={p.id} className="rounded-soft border border-[rgba(120,80,200,.25)] bg-[rgba(120,80,200,.05)] p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-[var(--ink)]">{formatearFecha(p.fecha)}</span>
                    <span className="text-[11px] text-[var(--ink-3)]">{p.solicitante_nombre || 'Administrador'}</span>
                  </div>
                  {p.motivo && <p className="text-[12px] text-[var(--ink-2)] mt-1">{p.motivo}</p>}
                  <ListaOperaciones ops={p.operaciones} />
                  {rechazoId === p.id && (
                    <input value={comentarioRechazo} onChange={(e) => setComentarioRechazo(e.target.value)} placeholder="Motivo del rechazo (opcional)"
                      className="w-full mt-2 px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] focus:outline-none" />
                  )}
                  <div className="flex gap-2 mt-2">
                    <Btn type="button" variant="primary" disabled={procesando === p.id} onClick={() => responder(p.id, 'aprobar')} className="!py-1 !px-2.5 text-[12px] flex items-center gap-1"><FiCheck size={12} /> Aceptar</Btn>
                    <Btn type="button" variant="ghost" disabled={procesando === p.id} onClick={() => rechazar(p.id)} className="!py-1 !px-2.5 text-[12px] flex items-center gap-1"><FiX size={12} /> {rechazoId === p.id ? 'Confirmar rechazo' : 'Rechazar'}</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mis solicitudes */}
        <div>
          <div className="eyebrow mb-2">Mis solicitudes</div>
          {solicitudes.length === 0 ? (
            <div className="flex items-center gap-2 text-[12px] text-[var(--ink-3)] py-2"><FiInbox size={14} /> No tienes solicitudes de corrección.</div>
          ) : (
            <div className="space-y-2">
              {solicitudes.slice(0, 8).map((s) => {
                const pill = estadoCorreccionPill(s.estado)
                return (
                  <div key={s.id} className="rounded-soft border border-[var(--line)] bg-[var(--bg-1)] p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-medium text-[var(--ink)]">{formatearFecha(s.fecha)}</span>
                      <div className="flex items-center gap-2">
                        <Pill tone={pill.tone}>{pill.label}</Pill>
                        {s.estado === 'pendiente' && (
                          <button type="button" disabled={procesando === s.id} onClick={() => cancelar(s.id)} className="text-red-400 hover:text-red-600" title="Cancelar"><FiTrash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                    {s.motivo && <p className="text-[12px] text-[var(--ink-2)] mt-1">{s.motivo}</p>}
                    <ListaOperaciones ops={s.operaciones} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Card>

      {modalOpen && (
        <CorreccionDiaModal
          modo="empleado"
          onClose={() => setModalOpen(false)}
          onSuccess={() => { cargar(); onCambio?.() }}
        />
      )}
    </>
  )
}

export default CorreccionesDiaPanel
